/**
 * SKNNYCHEF Theme — theme.js
 * Deferred non-critical JavaScript
 */

(function () {
  'use strict';

  /* =========================================================
     1. Floating CTA — show on scroll-down past 400px,
        hide on scroll-up (as per design spec)
     ========================================================= */
  function initFloatingCTA() {
    var cta = document.getElementById('floating-cta');
    if (!cta) return;

    var THRESHOLD = 400;
    var lastScrollY = window.scrollY;
    var ticking = false;

    function updateCTA() {
      var currentScrollY = window.scrollY;
      var scrollingDown = currentScrollY > lastScrollY;

      if (currentScrollY > THRESHOLD && scrollingDown) {
        // Past threshold and scrolling down — show
        cta.classList.add('is-visible');
        document.body.classList.add('floating-cta-visible');
      } else if (currentScrollY <= THRESHOLD || !scrollingDown) {
        // Above threshold or scrolling up — hide
        cta.classList.remove('is-visible');
        document.body.classList.remove('floating-cta-visible');
      }

      lastScrollY = currentScrollY;
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(updateCTA);
        ticking = true;
      }
    }, { passive: true });

    // Run once on init
    updateCTA();
  }

  /* =========================================================
     2. Animate on scroll — IntersectionObserver with fallback
        Handles both .aos-* classes and .reveal-element classes
     ========================================================= */
  function initAOS() {
    var selector = '.aos-fade-up, .aos-fade-in, .reveal-element';
    var elements = document.querySelectorAll(selector);
    if (!elements.length) return;

    // Honor prefers-reduced-motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      for (var m = 0; m < elements.length; m++) {
        elements[m].classList.add('is-visible');
      }
      return;
    }

    if (!('IntersectionObserver' in window)) {
      // Fallback: make all elements visible immediately
      for (var i = 0; i < elements.length; i++) {
        elements[i].classList.add('is-visible');
      }
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        for (var j = 0; j < entries.length; j++) {
          var entry = entries[j];
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    for (var k = 0; k < elements.length; k++) {
      observer.observe(elements[k]);
    }
  }

  /* =========================================================
     3. Mobile nav toggle (backup — also inlined in theme.liquid)
     ========================================================= */
  function initMobileNav() {
    var toggle = document.querySelector('.nav-toggle');
    var mobileNav = document.getElementById('mobile-nav');
    if (!toggle || !mobileNav) return;

    // Guard against duplicate listeners from inline script
    if (toggle.dataset.navInit) return;
    toggle.dataset.navInit = 'true';

    function closeMobileNav() {
      mobileNav.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    toggle.addEventListener('click', function () {
      var isOpen = mobileNav.classList.contains('is-open');
      mobileNav.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(!isOpen));
      document.body.style.overflow = isOpen ? '' : 'hidden';
    });

    mobileNav.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', closeMobileNav);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && mobileNav.classList.contains('is-open')) {
        closeMobileNav();
        toggle.focus();
      }
    });
  }

  /* =========================================================
     4. FAQ accordion — button-based toggle (.faq-item__trigger)
        Used on community-landing page. Only one open at a time.
     ========================================================= */
  function initFAQ() {
    var triggers = document.querySelectorAll('.faq-item__trigger');
    if (!triggers.length) return;

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var isExpanded = trigger.getAttribute('aria-expanded') === 'true';
        var body = trigger.nextElementSibling;

        // Close all others first
        triggers.forEach(function (otherTrigger) {
          if (otherTrigger !== trigger) {
            otherTrigger.setAttribute('aria-expanded', 'false');
            var otherBody = otherTrigger.nextElementSibling;
            if (otherBody) {
              otherBody.hidden = true;
              otherBody.style.maxHeight = '';
            }
          }
        });

        // Toggle current
        if (isExpanded) {
          trigger.setAttribute('aria-expanded', 'false');
          if (body) {
            body.hidden = true;
            body.style.maxHeight = '';
          }
        } else {
          trigger.setAttribute('aria-expanded', 'true');
          if (body) {
            body.hidden = false;
            // Animate open
            body.style.maxHeight = body.scrollHeight + 'px';
          }
        }
      });
    });

    // Initialize: ensure bodies that are hidden have correct style
    triggers.forEach(function (trigger) {
      var body = trigger.nextElementSibling;
      if (body && body.hidden) {
        body.style.maxHeight = '';
      }
    });
  }

  /* =========================================================
     5. Lazy load iframes (video embeds)
     ========================================================= */
  function initLazyIframes() {
    var iframes = document.querySelectorAll('iframe[loading="lazy"]');
    if (!iframes.length) return;

    if (!('IntersectionObserver' in window)) return;

    var iframeObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var iframe = entry.target;
            if (iframe.dataset.src) {
              iframe.src = iframe.dataset.src;
            }
            iframeObserver.unobserve(iframe);
          }
        });
      },
      { rootMargin: '200px' }
    );

    iframes.forEach(function (iframe) {
      iframeObserver.observe(iframe);
    });
  }

  /* =========================================================
     6. Smooth scroll for anchor links
     ========================================================= */
  function initSmoothScroll() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href^="#"]');
      if (!link) return;

      var targetId = link.getAttribute('href').slice(1);
      if (!targetId) return;

      var target = document.getElementById(targetId);
      if (!target) return;

      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Update URL hash without scroll jump
      if (history.pushState) {
        history.pushState(null, null, '#' + targetId);
      }
    }, { passive: false });
  }

  /* =========================================================
     Init all
     ========================================================= */
  function init() {
    initFloatingCTA();
    initAOS();
    initMobileNav();
    initFAQ();
    initLazyIframes();
    initSmoothScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
